/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef, HostListener,
  Input, OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationNode } from './navigation.interfaces';
import { NavigationService } from './navigation.service';
import { Location } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { coalesce, parseUrl, toString } from '@gh/core-util';
import { flatMap, isEmpty, tail } from 'lodash';
import { distinctUntilChanged$, filter$, map$ } from '@gh/rx/operators';
import { Subscription } from 'rxjs';

function getSnapshotUrl(snapshot: ActivatedRouteSnapshot): string {
  return flatMap(tail(snapshot.pathFromRoot), (snapshotInPath) => snapshotInPath.url.map(toString)).join('/');
}

/**
 * Finds snapshot in snapshot tree which belongs to the given <code>route</code>
 *
 * @param topSnapshot
 * @param route
 */
function findSnapshotForRoute(topSnapshot: ActivatedRouteSnapshot, route: string): Maybe<ActivatedRouteSnapshot> {
  const stack: ActivatedRouteSnapshot[] = [topSnapshot];

  while (stack.length) {
    const snapshot = stack.pop();

    if (snapshot) {
      const url = getSnapshotUrl(snapshot);
      if (url === route) {
        return snapshot;
      } else if (url.indexOf(route) !== 0) {
        snapshot.children.forEach((childSnapshot) => stack.push(childSnapshot));
      }
    }
  }

  return;
}

function isNavigationToRouteAllowed(topSnapshot: ActivatedRouteSnapshot, route: string, reuseRoute: boolean): boolean {
  const snapshot = findSnapshotForRoute(topSnapshot, route);
  // if requested route is active and it is allowed to reuse => we run navigation only if route is not empty
  return snapshot ? reuseRoute && !isEmpty(snapshot.queryParams) : true;
}

@Component({
  standalone: false,
  selector: 'gh-navigation',
  templateUrl: 'navigation.component.html',
  styleUrls: ['navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationComponent implements OnInit, OnDestroy {
  @ViewChild('menu') menuRef: ElementRef<HTMLElement>;
  tree: NavigationNode;
  private subscription: Subscription;
  private currentUrl: string;
  private nonStandardUrlList =  [
    {
      url: (url: string): boolean => {
        const urlArray = url.split('/').filter((item) => !!item && !!item.length);
        return urlArray[0] === 'contacts';
      },
      correctUrl: '/contacts',
    },
    {
      url: (url: string): boolean => {
        const urlArray = url.split('/').filter((item) => !!item && !!item.length);
        if (urlArray.length < 5) {
          return false;
        }
        return urlArray[0] === 'sales' && urlArray[1] === 'sales-folders' && urlArray[3] === 'rfq' && urlArray[4] === 'quotations';
      },
      correctUrl: '/sales/rfq-management',
    },
    {
      url: (url: string): boolean => {
        const urlArray = url.split('/').filter((item) => !!item && !!item.length);
        if (urlArray.length < 4) {
          return false;
        }
        return urlArray[0] === 'sales' && urlArray[1] === 'sales-folders' && urlArray[3] === 'rfq';
      },
      correctUrl: '/sales/leads',
    },
    {
      url: (url: string): boolean => {
        const urlArray = url.split('/').filter((item) => !!item && !!item.length);
        if (urlArray.length < 4) {
          return false;
        }
        return urlArray[0] === 'job-admin' && urlArray[3].includes('color-selection');
      },
      correctUrl: '/job-admin/job-color-selection',
    },
    {
      url: (url: string): boolean => {
        const urlArray = url.split('/').filter((item) => !!item && !!item.length);
        if (urlArray.length < 4) {
          return false;
        }
        return urlArray[0] === 'job-admin' && urlArray[3].includes('spec');
      },
      correctUrl: '/job-admin/job-specifications',
    },
    {
      url: (url: string): boolean => {
        const urlArray = url.split('/').filter((item) => !!item && !!item.length);
        if (urlArray.length < 4) {
          return false;
        }
        return urlArray[0] === 'job-admin' && urlArray[3].includes('estimate');
      },
      correctUrl: '/estimating/job-estimates',
    },
  ];
  constructor(private renderer: Renderer2,
              private element: ElementRef,
              private router: Router,
              private location: Location,
              private changeDetectorRef: ChangeDetectorRef,
              private navigation: NavigationService) {

  }
  ngOnInit(): void {
    this.tree = this.navigation.tree;
    this.subscription = this.router.events.pipe(
      filter$((event) => event instanceof NavigationEnd),
      // track only pathname changes (changes in query params do not affect active navigation item)
      map$((event: NavigationEnd) => parseUrl(event.url).pathname),
      distinctUntilChanged$()).subscribe((res) => {
        if (!this.currentUrl || res !== this.currentUrl) {
          this.currentUrl = res;
          // this.subscription.unsubscribe();
          this.setInitState();
        }
      });
    // OnPush: items added / removed after the first render (e.g. "Switch Office", added once the accessible offices
    // load after login) must re-render the menu now, not only on the next route change.
    this.subscription.add(this.navigation.onChanged.subscribe(() => this.changeDetectorRef.markForCheck()));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  @Input()
  set compact(value: boolean) {
    if (value) {
      this.renderer.addClass(this.element.nativeElement, 'is-compact');
    } else {
      this.renderer.removeClass(this.element.nativeElement, 'is-compact');
    }
  }

  onLinkClick(item: NavigationNode): void {
    const { onClick, route, reuseRoute } = item.item;
    if (onClick) {
      onClick();
    } else if (route
      && isNavigationToRouteAllowed(this.router.routerState.snapshot.root, route, coalesce(reuseRoute, true))) {
      // We navigate to the requested route only if it is not active.
      this.router.navigate([route], { queryParams: {} });
    }
  }

  onToggle(event: any, item: NavigationNode): void {
    this.tree.children.forEach((child) => {
      if (item.item.name === child.item.name) {
        item.item.open = !item.item.open;
      } else {
        if (child.item.open) {
          child.item.open = false;
        }
      }
    });
    this.processAccordion();
  }

  getUrl(item: NavigationNode): string {
    return item.item.route ?  this.location.prepareExternalUrl(item.item.route) : '';
  }

  isOpen(item: NavigationNode): boolean {
    return !!item.item.open;
  }

  private setInitState(): void {
    this.tree.children.forEach((child) => {
      if (child.item.active && !!child.children.length) {
        child.children.forEach((subChild) => {
          subChild.item.active = false;
        });
      }
      child.item.open = false;
      child.item.active = false;
    });
    const currentUrl = this.getFixedCurrentUrl().split('/').filter((item) => !!item.length);
    if (!currentUrl.length) {
      this.changeDetectorRef.markForCheck();
      this.processAccordion();
      return;
    }

    const firstSegment = this.getClearRoute(currentUrl[0]);
    if (currentUrl.length > 1) {
      const parent = this.getParent(currentUrl[1], currentUrl);

      if (parent) {
        parent.item.open = true;
        parent.item.active = true;
        const child = parent.children.find((subChild) => subChild?.item?.route?.split('/')[1] === this.getClearRoute(currentUrl[1]));
        if (child?.item) {
          child.item.active = true;
        }
      } else {
        this.activateNestedItemByRoute(firstSegment);
      }
      this.changeDetectorRef.markForCheck();
      this.processAccordion();
      return;
    }
    const childItem = this.tree.children.find((item) => item.path === firstSegment ||
      item.item.route === firstSegment);
    if (childItem) {
      childItem.item.active = true;
    } else {
      this.activateNestedItemByRoute(firstSegment);
    }
    this.changeDetectorRef.markForCheck();
    this.processAccordion();
  }

  private getClearRoute(route: string): string {
    return route.includes('?') ? route.split('?')[0] : route;
  }

  private activateNestedItemByRoute(route: string): boolean {
    const parent = this.tree.children.find((child) =>
      !!child.children?.find((subChild) => subChild?.item?.route === route));
    if (!parent) {
      return false;
    }
    parent.item.open = true;
    parent.item.active = true;
    const child = parent.children.find((subChild) => subChild?.item?.route === route);
    if (child?.item) {
      child.item.active = true;
    }
    return true;
  }

  private getParent(route: string, url: string[]): NavigationNode | undefined {
    return this.tree.children.find((child) => {
      if (child.children && !!child.children.length) {
        return !!child.children.find((subChild) => subChild?.item?.route?.split('/')[1] === this.getClearRoute(route))
          && (child.item.route?.split('/')[0] === url[0] || child.path === url[0]) ;
      } else {
        return child.item.route === url.join('/');
      }
    });
  }
  private getFixedCurrentUrl(): string {
    const url = this.router.routerState.snapshot.url;
    const fixedUrl = this.nonStandardUrlList.find((item) => item.url(url));
    return fixedUrl ? fixedUrl.correctUrl : this.router.routerState.snapshot.url;
  }

  private processAccordion(): void {
    setTimeout(() => {
      const childrenList = this.menuRef.nativeElement.getElementsByClassName('children');
      for (let i = 0; i < childrenList.length; i++) {
        childrenList[i]['style'].maxHeight =  childrenList[i].className.includes('is-open') ?  childrenList[i].scrollHeight + 'px' : 0;
      }
    });
  }
}
